import React, { useState, useEffect, useCallback } from 'react';
import { fetchBookings } from '../../../services/apiService';

function formatDateTime(isoStr) {
  if (!isoStr) return '–';
  try {
    return new Date(isoStr)?.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return isoStr; }
}

function formatDate(dateStr) {
  if (!dateStr) return '–';
  try {
    const p = dateStr?.split('-'); if (p?.length === 3) return `${p[2]}.${p[1]}.${p[0]}`; return dateStr;
  } catch { return dateStr; }
}

const OutlookSyncStatus = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [retrying, setRetrying] = useState(null); // booking_ref being retried
  const [retryResults, setRetryResults] = useState({}); // booking_ref → { success, error, at }

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBookings();
      setBookings(data || []);
      setLastSyncAt(new Date()?.toISOString());
    } catch (e) {
      console.error('[OutlookSyncStatus] loadBookings error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleRetry = async (booking) => {
    const ref = booking?.booking_ref;
    setRetrying(ref);
    try {
      const res = await fetch('/api/create-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          beraterEmail: booking?.berater_email,
          beraterName: booking?.berater_name,
          date: booking?.date,
          time: booking?.time,
          consultationType: booking?.type,
          vorname: booking?.vorname,
          nachname: booking?.nachname,
          email: booking?.email,
          telefon: booking?.telefon,
          bookingRef: ref,
        }),
      });
      const data = await res?.json();
      if (data?.success && data?.eventId) {
        setRetryResults(prev => ({
          ...prev,
          [ref]: { success: true, eventId: data?.eventId, at: new Date()?.toISOString() },
        }));
        // Refresh list to pick up updated outlook_event_id
        await loadBookings();
      } else {
        setRetryResults(prev => ({
          ...prev,
          [ref]: { success: false, error: data?.error || 'Unbekannter Fehler', at: new Date()?.toISOString() },
        }));
      }
    } catch (err) {
      setRetryResults(prev => ({
        ...prev,
        [ref]: { success: false, error: err?.message || 'Netzwerkfehler', at: new Date()?.toISOString() },
      }));
    } finally {
      setRetrying(null);
    }
  };

  const synced = bookings?.filter(b => b?.outlook_event_id);
  const pending = bookings?.filter(b => !b?.outlook_event_id);

  return (
    <div style={{ background: '#fff', border: '1px solid #dde1e7', borderRadius: '16px', overflow: 'hidden', marginBottom: '28px' }}>
      {/* Header */}
      <div style={{ background: '#3E4954', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>Outlook Sync Status</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>Alle Buchungen mit Kalender-Synchronisierungsstatus</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {lastSyncAt && (
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '100px', fontWeight: 600 }}>
              🔄 Zuletzt: {formatDateTime(lastSyncAt)}
            </span>
          )}
          <button
            onClick={loadBookings}
            disabled={loading}
            style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Lädt...' : '↻ Aktualisieren'}
          </button>
        </div>
      </div>

      {/* Summary badges */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '12px', flexWrap: 'wrap', background: '#f8fafc' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '10px', background: '#dcfce7', border: '1px solid #86efac' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#16a34a' }}>{synced?.length} Synchronisiert</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '10px', background: '#fef3c7', border: '1px solid #fcd34d' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#d97706', display: 'inline-block' }} />
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#d97706' }}>{pending?.length} Ausstehend</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '10px', background: '#f1f5f9', border: '1px solid #dde1e7' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>{bookings?.length} Gesamt</span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>Lade Buchungen...</div>
      ) : bookings?.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📋</div>
          Noch keine Buchungen vorhanden
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f5f6f8' }}>
                {['Buchungs-Ref', 'Name', 'Berater', 'Datum / Uhrzeit', 'Art', 'Outlook Sync', 'Letzter Versuch', 'Aktion']?.map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7a8d', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid #dde1e7' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings?.map((b, idx) => {
                const isSynced = !!b?.outlook_event_id;
                const retryResult = retryResults?.[b?.booking_ref];
                const isRetrying = retrying === b?.booking_ref;
                return (
                  <tr key={b?.id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {b?.booking_ref || '–'}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1e2b38', whiteSpace: 'nowrap' }}>
                      {b?.vorname} {b?.nachname}
                      <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>{b?.email}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#1e2b38', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{b?.berater_name || '–'}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{b?.berater_email || ''}</div>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: '#1e2b38' }}>{formatDate(b?.date)}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{b?.time || '–'} Uhr</div>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: b?.type === 'onsite' ? '#ede9fe' : '#dbeafe', color: b?.type === 'onsite' ? '#7c3aed' : '#1d4ed8' }}>
                        {b?.type === 'onsite' ? 'Vor-Ort' : 'Telefon'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {isSynced ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }}>
                            ✓ Synchronisiert
                          </span>
                          {b?.outlook_web_link && (
                            <a href={b?.outlook_web_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>↗ Öffnen</a>
                          )}
                        </div>
                      ) : retryResult?.success ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }}>
                          ✓ Gerade synchronisiert
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, background: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d' }}>
                          ⏳ Ausstehend
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '11px', color: '#94a3b8' }}>
                      {retryResult ? (
                        <div>
                          <div style={{ color: retryResult?.success ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                            {retryResult?.success ? '✓ Erfolgreich' : '✗ Fehler'}
                          </div>
                          <div style={{ color: '#94a3b8' }}>{formatDateTime(retryResult?.at)}</div>
                          {retryResult?.error && (
                            <div style={{ color: '#dc2626', fontSize: '10px', maxWidth: '160px', wordBreak: 'break-word' }}>{retryResult?.error}</div>
                          )}
                        </div>
                      ) : isSynced ? (
                        <span style={{ color: '#16a34a' }}>Bei Buchung</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>–</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {!isSynced && !retryResult?.success && (
                        <button
                          onClick={() => handleRetry(b)}
                          disabled={isRetrying}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '8px',
                            border: '1.5px solid #3E4954',
                            background: isRetrying ? '#f1f5f9' : '#fff',
                            color: '#3E4954',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: isRetrying ? 'not-allowed' : 'pointer',
                            opacity: isRetrying ? 0.6 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          {isRetrying ? (
                            <><span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid #3E4954', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Wird synchronisiert...</>
                          ) : (
                            <>↻ Erneut versuchen</>
                          )}
                        </button>
                      )}
                      {retryResult?.success && (
                        <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>✓ Fertig</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default OutlookSyncStatus;
